import apiClient from "../utils/axios";

export interface BillingStatus {
  access_allowed: boolean;
  reason?: string;
  subscription_status?: string;
  payment_status?: string;
  billing_mode?: string;
  days_overdue?: number;
  days_until_suspension?: number;
  payment_due_date?: string;
  suspension_date?: string;
  overdue_since?: string;
}

export interface BillingModeRequest {
  billing_mode: 'CreditCard' | 'Invoice';
}

export class BillingService {
  
  static async getBillingStatus(
    organizationId: string,
    token: string
  ): Promise<BillingStatus> {
    const response = await apiClient.get(
      `/api/organization/${organizationId}/billing/status`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async updateBillingMode(
    organizationId: string,
    billingMode: 'CreditCard' | 'Invoice',
    token: string
  ): Promise<{ success: boolean; message: string; billing_mode: string; payment_status: string }> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/billing/mode`,
      { billing_mode: billingMode },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async createCustomerPortalSession(
    organizationId: string,
    token: string
  ): Promise<{ portal_url: string }> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/billing/customer-portal`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async markInvoicePaid(
    organizationId: string,
    token: string
  ): Promise<{ success: boolean; message: string; payment_date: string; payment_status: string; subscription_status: string }> {
    const response = await apiClient.post(
      `/api/organization/${organizationId}/billing/mark-paid`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static redirectToCustomerPortal(portalUrl: string): void {
    window.open(portalUrl, '_blank');
  }

  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  static getPaymentStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'current':
        return 'green';
      case 'overdue':
        return 'orange';
      case 'suspended':
        return 'red';
      default:
        return 'default';
    }
  }

  static getBillingModeIcon(mode: string) {
    switch (mode?.toLowerCase()) {
      case 'creditcard':
        return 'CreditCardOutlined';
      case 'invoice':
        return 'FileTextOutlined';
      default:
        return 'DollarOutlined';
    }
  }

  static getBillingModeDescription(mode: string): string {
    switch (mode?.toLowerCase()) {
      case 'creditcard':
        return 'Automatic credit card billing through Stripe';
      case 'invoice':
        return 'Manual invoice payment with NET-30 terms';
      default:
        return 'Unknown billing mode';
    }
  }
} 